import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Paper,
  Chip,
  Button,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardDoubleArrowUp as ScrollUpIcon,
  Pause as PauseIcon,
  TouchApp as ActivityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import debounce from 'lodash.debounce';
import type { DeviceLog } from '@/types';
import { colors } from '@/theme/tokens';

interface ActivityRowProps {
  activity: DeviceLog;
  isExpanded: boolean;
  onToggle: () => void;
}

function ActivityRow({ activity, isExpanded, onToggle }: ActivityRowProps) {
  const timestamp = new Date(activity.created_at).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const packageName = (activity.metadata?.packageName as string) || '';
  const activityName = (activity.metadata?.activityName as string) || '';

  // Extract short class name (last part after .)
  const shortPackageName = packageName.split('.').pop() || packageName;
  const shortActivityName = activityName.split('.').pop() || activityName;

  return (
    <Box
      sx={{
        borderBottom: '1px solid #EEEEEE',
        '&:hover': { bgcolor: '#F5F5F5' },
      }}
    >
      {/* Main row - clickable */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          fontFamily: 'monospace',
          fontSize: 12,
        }}
      >
        {/* Expand icon */}
        <IconButton size="small" sx={{ p: 0 }}>
          {isExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: '#9E9E9E' }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: '#9E9E9E' }} />
          )}
        </IconButton>

        {/* Activity icon */}
        <ActivityIcon sx={{ color: '#9E9E9E', fontSize: 18, flexShrink: 0 }} />

        {/* Timestamp */}
        <Typography sx={{ color: '#757575', fontSize: 12, fontFamily: 'monospace', minWidth: 70, flexShrink: 0 }}>
          {timestamp}
        </Typography>

        {/* Package name badge */}
        <Chip
          label={shortPackageName}
          size="small"
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 500,
            bgcolor: '#E3F2FD',
            color: '#1976D2',
            flexShrink: 0,
            maxWidth: 120,
            '& .MuiChip-label': { px: 1 },
          }}
          title={packageName}
        />

        {/* Activity name */}
        <Typography
          sx={{
            flex: 1,
            fontSize: 12,
            fontFamily: 'monospace',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={activityName}
        >
          {shortActivityName}
        </Typography>
      </Box>

      {/* Expanded details */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: '#FAFAFA',
            borderTop: '1px solid #EEEEEE',
            fontSize: 12,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography sx={{ color: '#757575', minWidth: 80, fontWeight: 500 }}>Package:</Typography>
              <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{packageName || '-'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography sx={{ color: '#757575', minWidth: 80, fontWeight: 500 }}>Activity:</Typography>
              <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{activityName || '-'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography sx={{ color: '#757575', minWidth: 80, fontWeight: 500 }}>Time:</Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>
                {new Date(activity.created_at).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

interface ActivityMonitorProps {
  activities: DeviceLog[];
  // onClear kept in the signature so existing callers don't break; the UI's
  // Clear button was removed with the counter.
  isLive?: boolean;
  onClear?: () => void;
  height?: number;
}

export function ActivityMonitor({ activities, isLive = false, height = 400 }: ActivityMonitorProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Auto-scroll state
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [newActivitiesCount, setNewActivitiesCount] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevActivitiesLengthRef = useRef(0);
  const isUserScrollingRef = useRef(false);

  // Debounce search
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setDebouncedSearch(value), 300),
    []
  );

  useEffect(() => {
    debouncedSetSearch(search);
    return () => debouncedSetSearch.cancel();
  }, [search, debouncedSetSearch]);

  // Deduplicate activities based on timestamp + package + activity
  const deduplicatedActivities = useMemo(() => {
    const seen = new Set<string>();
    return activities.filter((activity) => {
      const packageName = (activity.metadata?.packageName as string) || '';
      const activityName = (activity.metadata?.activityName as string) || '';
      // Create a unique key based on timestamp (rounded to second) + package + activity
      const timestampSec = Math.floor(new Date(activity.created_at).getTime() / 1000);
      const key = `${timestampSec}-${packageName}-${activityName}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [activities]);

  // Filter activities
  const filteredActivities = useMemo(() => {
    if (!debouncedSearch) return deduplicatedActivities;
    const searchLower = debouncedSearch.toLowerCase();
    return deduplicatedActivities.filter((activity) => {
      const packageName = (activity.metadata?.packageName as string) || '';
      const activityName = (activity.metadata?.activityName as string) || '';
      return (
        packageName.toLowerCase().includes(searchLower) ||
        activityName.toLowerCase().includes(searchLower)
      );
    });
  }, [deduplicatedActivities, debouncedSearch]);

  // Handle scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !isLive) return;

    const { scrollTop } = scrollContainerRef.current;
    const isAtTop = scrollTop < 50;

    if (!isAtTop && !isUserScrollingRef.current) {
      isUserScrollingRef.current = true;
      setIsAutoScroll(false);
    } else if (isAtTop && isUserScrollingRef.current) {
      isUserScrollingRef.current = false;
      setIsAutoScroll(true);
      setNewActivitiesCount(0);
    }
  }, [isLive]);

  // Auto-scroll when new activities arrive
  useEffect(() => {
    if (!isLive) return;

    const newAdded = deduplicatedActivities.length - prevActivitiesLengthRef.current;
    prevActivitiesLengthRef.current = deduplicatedActivities.length;

    if (newAdded > 0) {
      if (isAutoScroll && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      } else {
        setNewActivitiesCount((prev) => prev + newAdded);
      }
    }
  }, [deduplicatedActivities.length, isAutoScroll, isLive]);

  // Reset auto-scroll when switching modes
  useEffect(() => {
    if (!isLive) {
      queueMicrotask(() => {
        setIsAutoScroll(true);
        setNewActivitiesCount(0);
      });
      isUserScrollingRef.current = false;
    }
  }, [isLive]);

  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
      setIsAutoScroll(true);
      setNewActivitiesCount(0);
      isUserScrollingRef.current = false;
    }
  }, []);

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <TextField
          placeholder="Search package or activity..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 150, maxWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: { fontSize: 13 },
          }}
        />

        {/* Stats + Pause indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {isLive && !isAutoScroll && (
            <Chip
              icon={<PauseIcon sx={{ fontSize: 14 }} />}
              label={newActivitiesCount > 0 ? `Paused (${newActivitiesCount} new)` : 'Paused'}
              size="small"
              onClick={scrollToTop}
              sx={{
                height: 22,
                fontSize: 11,
                bgcolor: '#FFF3E0',
                color: '#F57C00',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#FFE0B2' },
              }}
            />
          )}
        </Box>
      </Box>

      {/* Activity list */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        {filteredActivities.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#9E9E9E' }}>
            <ActivityIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography>
              {deduplicatedActivities.length === 0
                ? (isLive ? 'Waiting for activity changes...' : 'No activities recorded')
                : 'No matching activities'}
            </Typography>
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{ height, overflow: 'auto' }}
          >
            {filteredActivities.map((activity) => (
              <ActivityRow
                key={activity.id}
                activity={activity}
                isExpanded={expandedId === activity.id}
                onToggle={() => handleToggleExpand(activity.id)}
              />
            ))}
          </Box>
        )}

        {/* Floating button to resume auto-scroll */}
        {isLive && newActivitiesCount > 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ScrollUpIcon />}
            onClick={scrollToTop}
            sx={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: colors.primary.main,
              '&:hover': { bgcolor: colors.primary.hover },
              textTransform: 'none',
              fontSize: 12,
              py: 0.5,
              boxShadow: 2,
            }}
          >
            {newActivitiesCount} new activit{newActivitiesCount > 1 ? 'ies' : 'y'}
          </Button>
        )}
      </Paper>
    </Box>
  );
}

export default ActivityMonitor;
